'use client';

import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { accessFromToken, type SportsAccess } from '@/lib/access';
import { isSigningInOrOut } from '@/lib/auth/thisTab';
import { createClient } from '@/lib/supabase/client';

// The local Supabase session as the app holds it (no network: getSession reads storage): who is signed in, and the
// access their token carries, the first-paint hint useAccess reads. Identity is not network-validated here: a session
// revoked server-side shows as signed in until an authed query 401s (QueryProvider then signs out and redirects); the
// server never serves it paid data meanwhile.
interface HeldSession {
  user: User | null;
  access: SportsAccess;
}
export const heldSession = (session: Session | null): HeldSession => ({ user: session?.user ?? null, access: accessFromToken(session?.access_token) });

export const sessionQuery = queryOptions({
  queryKey: ['session'] as const,
  queryFn: async () => heldSession((await createClient().auth.getSession()).data.session),
  staleTime: Infinity,
  // Kept while nothing reads it: the auth listener compares each change with the user it holds.
  gcTime: Infinity,
  retry: 1,
});

const selectUser = (s: HeldSession) => s.user;

// Who is signed in. Access questions go through useAccess.
export function useSession(): { user: User | null; loading: boolean } {
  const { data, isLoading } = useQuery({ ...sessionQuery, select: selectUser });
  return { user: data ?? null, loading: isLoading };
}

// The app's one auth listener (QueryProvider mounts it): keeps the session current and, when another tab signs a
// different user in or out (this tab marks its own, lib/auth/thisTab), reloads, as a sign-in or out here does (a full
// page load), so nothing the previous user loaded stays in this tab. Returns the unsubscribe.
export function syncSession(queryClient: QueryClient, supabase: SupabaseClient): () => void {
  // The listener's first write (INITIAL_SESSION, on subscribing) may create the entry before any reader: keep it too.
  queryClient.setQueryDefaults(sessionQuery.queryKey, { staleTime: Infinity, gcTime: Infinity });
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    const next = heldSession(session);
    const prev = queryClient.getQueryData(sessionQuery.queryKey);
    if (prev && prev.user?.id !== next.user?.id && !isSigningInOrOut()) {
      window.location.reload();
      return;
    }
    queryClient.setQueryData(sessionQuery.queryKey, next);
  });
  return () => subscription.unsubscribe();
}
